import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Button, Paper, Box, TextField, Autocomplete, IconButton, Dialog, DialogContent, DialogActions } from '@mui/material';
import { Save, PlaylistPlay } from "@mui/icons-material";
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { CellFocusedEvent } from 'ag-grid-community';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useGetCRMDashboardMutation, useGetSMSContentMutation } from '../../services/consumer';
import { usePhuTranMutation } from '../../services/home';
import LoadingPage from '../loadingPage';
import AlertSnackbar from '../alertSnackbar';
import "react-day-picker/style.css";
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DateRange from '../tools/daterange';
import AutocompleteWithAll from '../tools/autocompleteWithAll';
import TableResult from './tableResult';
const API_URI = process.env.REACT_APP_API_URI;

export default function PhuTran() {
  const [phutran, phutranResult] = usePhuTranMutation();
  const [dataLoaded, setDataLoaded] = useState([]);
  const [execResult, setExecResult] = useState([]);
  const [columns, setCoumns] = useState([]);
  const [gridHeight, setGridHeight] = useState(300);
  const [snackContent, setSnackContent] = useState("");
  const [range, setRange] = useState({from: moment().startOf('month'), to: moment()});
  const [selectedRecord, setSelectedRecord] = useState(null);
  const gridRef = useRef();
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([
      { make: 'Total', model: '', TOTAL: 0 }
  ]);

  ModuleRegistry.registerModules([ AllCommunityModule ]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    wrapText: true,     
    autoHeight: true,   
    filter: true,
    flex: 1,
    cellStyle: { lineHeight: '1.4' } 
  }), []);

  useEffect(() => {
    getDataView();
  }, []);

  const handleGridHeight = () =>{
    const divHeader = document.getElementById('divHeader');
    setGridHeight(window.innerHeight - (divHeader?divHeader.clientHeight:0) - 52);
  }

  const getDataView = async() =>{
    let encodedData = encodeURIComponent("select ID, QUERY from PHUTRAN union all select 0 ID, '' QUERY");
    let result = await phutran({"type": 'exec', query: encodedData}).unwrap();
    if(typeof result === "string")
					result = JSON.parse(result);

    if(result.length > 0)
      getCoumns(result[0]);
    
    setDataLoaded(result);
    handleGridHeight();
  }

  const handleSaveClick = async (rowDataInstance) => {
    if(rowDataInstance.ID){
      const query = `update PHUTRAN set QUERY = '${rowDataInstance.QUERY.replace(/'/g, "''")}' where ID = ${rowDataInstance.ID}`;
      let encodedData = encodeURIComponent(query);
      let result = await phutran({"type": 'exec', "query": encodedData}).unwrap();
    }
    else{
      const query = `insert into PHUTRAN(QUERY) values('${rowDataInstance.QUERY.replace(/'/g, "''")}')`;
      let encodedData = encodeURIComponent(query);
      let result = await phutran({"type": 'exec', "query": encodedData}).unwrap();
    }
    getDataView();
  }

  const execQuery = async () => {
    let encodedData = encodeURIComponent(selectedRecord.QUERY);
    let result = await phutran({"type": 'exec', query: encodedData}).unwrap();
    setExecResult(result);
  }

  const getCoumns=(item)=>{
    const cols = Object.keys(item).map((key) => {
      const col = {
        field: key,
        hide: key === "ID"
      };

      if (key === "QUERY") {
        col.editable = true;
        col.cellEditor = 'agLargeTextCellEditor';
        col.cellEditorPopup = true;
        
        // Wrap & spacing rules are isolated to just this one column
        col.wrapText = true;
        col.autoHeight = true; 
        col.cellStyle = { 
          lineHeight: '1.6',       
          paddingTop: '12px',      
          paddingBottom: '12px',   
          whiteSpace: 'pre-wrap'   
        };

        col.suppressKeyboardEvent = (params) => {
          const event = params.event;
          if (event.key === 'Enter' && !event.shiftKey) {
            return true; 
          }
          return false;
        };

        col.cellEditorParams = {
          maxLength: 4000,
          isMaxCharCountSet: true // Built-in counter for agLargeTextCellEditor
        };
      }

      return col;
    });

    const actionCol = {
      headerName: '',
      field: 'actions_column',
      width: 60,
      suppressSizeToFit: true, // Keep button width locked down
      sortable: false,
      filter: false,
      editable: false,
      pinned: 'right',
      cellRenderer: (params) => {
        return (
          <>
          <IconButton onClick={() => handleSaveClick(params.data)} size="small">
            <Save fontSize="small" />
          </IconButton>
          <IconButton onClick={() => setSelectedRecord(params.data)} size="small">
            <PlaylistPlay fontSize="small" />
          </IconButton>
          </>
        );
      }
    };

    setCoumns([...cols, actionCol]);
  }

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
  }, []);

  const getRowClass = useCallback(params => {
    if (params.node.rowIndex % 2 === 1) {
        return "grid-odd-rows";
    }
    if (params.node.rowPinned === 'bottom') {
      return "text-bold";
    }
  }, []);

  const myTheme = themeQuartz.withParams({
    // borderColor: "#9696C8",
    wrapperBorder: false,
    headerRowBorder: false,
    // rowBorder: { style: "dotted", width: 1 },
    columnBorder: { style: "dashed", color: '#9696C8' },
    headerColumnBorder: { style: 'solid', width: 1, color: '#ccc' },
  });

  const theme = useMemo(() => {
    return myTheme;
  }, []);

  const handleCellEditRequest = useCallback((event) => {
    const { rowIndex, column, newValue, data } = event;
    const field = column.getColId();
    const newRowData = [...dataLoaded];
    newRowData[rowIndex] = {
      ...data,
      [field]: newValue
    };
    setDataLoaded(newRowData);
  }, [dataLoaded]);

  const onChangeQuery = (newValue) => {
    const newRowData = [...dataLoaded];
    newRowData.find(f=>f.ID == selectedRecord.ID).QUERY = newValue;
    setDataLoaded(newRowData);
  }

  return (
    <div style={{padding: '0'}}>
      { (phutranResult.isLoading) && <LoadingPage /> }
      <div style={{marginTop: '0px'}}>
        {/* <div id='divHeader' style={{float: 'left'}}>
          <div style={{float: 'left', display: 'flex'}}>
            <Button variant="contained" onClick={getDataView}
              sx={{ marginRight: '10px', marginBottom: '10px', marginLeft: '10px', float: 'left' }}>View</Button>
          </div>
        </div> */}
        <Paper sx={{ height: gridHeight, width: '100%', float: 'left' }}>
          <AgGridReact enableCellTextSelection={true} ensureDomOrder={true}
            rowData={dataLoaded}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={500}
            paginationPageSizeSelector={[200, 500, 1000]}
            // pinnedBottomRowData={pinnedBottomRowData}
            getRowClass={getRowClass}
            theme={theme}
            onGridReady={onGridReady}
            singleClickEdit={true}
            stopEditingWhenCellsLoseFocus={true}
            readOnlyEdit={true} 
            onCellEditRequest={handleCellEditRequest}
          />
        </Paper>
      </div>

      <Dialog
        fullScreen={true}
        open={selectedRecord ? true : false}
        onClose={()=>{setSelectedRecord(null); setExecResult([]);}}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogContent style={{padding: 0}}>
          { (phutranResult.isLoading) && <LoadingPage /> }
          <TableResult record={selectedRecord} data={execResult} onChangeQuery={onChangeQuery} />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={()=>{setSelectedRecord(null); setExecResult([]);}}>
            CLOSE
          </Button>
          <Button onClick={execQuery}>
            RUN
          </Button>
        </DialogActions>
      </Dialog>

      <AlertSnackbar message={snackContent} setSnackContent={setSnackContent}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} />
    </div>
  )
}